/**
 * Universal accessor for public environment variables.
 *
 * This is a static-export app: public env values are baked into the bundle
 * at BUILD time via NEXT_PUBLIC_ENV_MAP (see next.config.ts). There is no
 * runtime injection — set these vars in the build environment (CI).
 *
 * - Client: reads NEXT_PUBLIC_ENV_MAP (a static reference that Turbopack/
 *   DefinePlugin replaces with a JSON string literal at build time)
 * - Server (build/prerender + node tests): reads process.env[key], falls
 *   back to process.env[NEXT_PUBLIC_*]
 *
 * Uses || (not ??): empty strings are treated as unset and fall through
 * to the next source — next.config.ts writes "" for unset keys.
 */

export const PUBLIC_ENV_KEYS = [
  "SENTRY_DSN",
  "SENTRY_TRACES_SAMPLE_RATE",
  "APP_ENV",
] as const;

export type PublicEnvKey = (typeof PUBLIC_ENV_KEYS)[number];

const NEXT_PUBLIC_PREFIX = "NEXT_PUBLIC_" as const;

// Lazy-parsed build-time env map. In the browser, process.env.NEXT_PUBLIC_ENV_MAP
// is a *static* reference that Turbopack/DefinePlugin replaces with a JSON string
// literal at build time. Dynamic bracket access (process.env[`NEXT_PUBLIC_${key}`])
// does NOT get replaced and evaluates to undefined on the client.
let buildTimeEnvMap: Record<string, string> | null = null;

function getBuildTimeEnvMap(): Record<string, string> {
  if (buildTimeEnvMap !== null) return buildTimeEnvMap;

  let parsed: Record<string, string> = {};

  // Server-side: not needed (we use direct process.env access)
  if (typeof window !== "undefined") {
    try {
      // MUST be a static reference — DefinePlugin replaces this literal at build time
      const raw = process.env.NEXT_PUBLIC_ENV_MAP;
      if (raw) parsed = JSON.parse(raw);
    } catch (e) {
      // console.error used intentionally: this runs during early initialization
      // (potentially before Sentry/logging is available). A malformed env map
      // means ALL public env vars will be unavailable on the client.
      console.error(
        "[env-config] Failed to parse NEXT_PUBLIC_ENV_MAP — " +
          "all public env vars will be unavailable on the client. " +
          "Check next.config.ts env map generation.",
        e
      );
    }
  }

  buildTimeEnvMap = parsed;
  return buildTimeEnvMap;
}

export function getPublicEnv(key: PublicEnvKey): string | undefined {
  if (typeof window !== "undefined") {
    // Client-side: build-time env map (static NEXT_PUBLIC_ENV_MAP reference)
    return getBuildTimeEnvMap()[key] || undefined;
  }

  // Server-side (build/prerender, node tests): prefer non-prefixed,
  // fall back to NEXT_PUBLIC_*
  return (
    process.env[key] ||
    process.env[`${NEXT_PUBLIC_PREFIX}${key}`] ||
    undefined
  );
}
